import { Box, Button, Flex, Text } from '@chakra-ui/react';
import type { ClaimedVnoSimpleLineChartProps } from '@ui/components/ClaimedVnoSimpleLineChart';
import { ClaimedVnoSimpleLineChart } from '@ui/components/ClaimedVnoSimpleLineChart';
import { InDesktop, useIsDesktop } from '@ui/components/MobileOrDesktop';
import usePendingVno from '@ui/hooks/usePendingVno';
import useVenoEarnings from '@ui/hooks/useVenoEarnings';
import { useTranslations } from '@ui/i18n';
import { MAXW_DESKTOP_CTA } from '@ui/utils/constants';
import type { GardenType } from '@ui/utils/garden';
import React, { useMemo } from 'react';

import { useGardenHarvestDrawer } from './GardenHarvestDrawer/useGardenHarvestDrawer';
import { GardenLogo } from './GardenLogo';
import {
  GardenVnoAllocationInfo,
  GardenVnoAllocationTooltip,
} from './GardenVnoAllocationInfo';
import { useGardenModalStore } from './useGardenModalStore';

export type GardenViewPlantProps = {
  gardenType: GardenType;
};

function GardenViewHarvest({ gardenType }: GardenViewPlantProps) {
  const t = useTranslations();
  const isDesktop = useIsDesktop();
  const { pendingVno } = usePendingVno(gardenType);
  const { historicalData } = useVenoEarnings('GARDEN');
  const coinMode = useGardenModalStore((s) => s.coinMode);
  const colorTheme = useGardenModalStore((s) => s.colorTheme);

  const openHarvestDrawer = useGardenHarvestDrawer((s) => s.onOpen);

  const claimedVnoList: ClaimedVnoSimpleLineChartProps['dataList'] = useMemo(
    () =>
      historicalData
        ? historicalData.map((d) => {
            return {
              claimedVno: d.valueBigNumber,
              date: d.date.getTime(),
            };
          })
        : [],
    [historicalData],
  );

  return (
    <Flex
      flexDir="column"
      px={4}
      pb="40px"
      maxW={{
        base: 'auto',
        desktop: '500px',
      }}
      marginX="auto"
    >
      <InDesktop>
        <GardenLogo coinMode={coinMode} gardenType={gardenType} />
      </InDesktop>
      <GardenVnoAllocationInfo
        vnoAmountTitle={t('Pending rewards')}
        vnoAmount={pendingVno}
        allocationTitle={
          <Box w="full" flex="1">
            {!!claimedVnoList?.length && (
              <ClaimedVnoSimpleLineChart
                colorTheme={colorTheme}
                dataList={claimedVnoList}
                legendLabel={
                  isDesktop ? undefined : t(`VNO claimed in Veno gardens`)
                }
                mb="36px"
              />
            )}
            <Flex alignItems="center">
              <Text>
                {t('{coin} distribution', {
                  coin: 'VNO',
                })}
              </Text>
              <GardenVnoAllocationTooltip />
            </Flex>
          </Box>
        }
        intoWalletTitle={t('Receive in wallet')}
        into12MonthTitle={t('Lock for 12 months')}
      />
      <Button
        variant="primary"
        disabled={!pendingVno || pendingVno.lte(0)}
        w={{ base: 'full', desktop: MAXW_DESKTOP_CTA }}
        mx={{ desktop: 'auto' }}
        mt="24px"
        onClick={() => {
          openHarvestDrawer({
            coinMode,
            gardenType,
          });
        }}
      >
        {t('Harvest')}
      </Button>
    </Flex>
  );
}

export default GardenViewHarvest;
